import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { UserPermissionsGridsService } from './user-permissions-grid.service';

@Component({
  selector: 'app-user-permissions-grid',
  templateUrl: './user-permissions-grid.component.html',
  styleUrls: ['./user-permissions-grid.component.scss'],
  providers: [GridOptionsService, UserPermissionsGridsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserPermissionsGridComponent implements OnInit {
  private translate = inject(TranslateService);
  gridOptionService = inject(GridOptionsService);
  private userPermissionsGridsService = inject(UserPermissionsGridsService);

  @Input() entityChanged$: BehaviorSubject<string>;
  @Input() shouldRefresh$: BehaviorSubject<boolean>;

  actions: DvToolbarAction[] = [];
  gridOptions: GridOptions | undefined;
  entityId: string;

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setupEvents();
  }

  private setupEvents(): void {
    this.entityChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(entityId => {
        this.entityId = entityId;

        if (!this.gridOptions) {
          this.setupVariables();
        }

        this.gridOptions!.context.extraFilter = this.getFilterModel(
          this.entityId
        );
        this.gridOptions?.context.loadClientSideData?.(
          this.getFilterModel(entityId),
          true
        );
        this.gridOptions!.rowGroupPanelShow = 'never';
      });

    // if roles got updated (add/delete), reload the user permissions grid
    this.shouldRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(shouldRefresh => {
        if (shouldRefresh) {
          this.gridOptions?.context.loadClientSideData?.(
            this.getFilterModel(this.entityId)
          );
        }
      });
  }

  private setupVariables(): void {
    this.gridOptions = {
      ...this.gridOptionService.clientSide(
        this.userPermissionsGridsService.colDefs,
        Entity.UserPermission,
        this.getFilterModel(this.entityId)
      ),
      suppressDragLeaveHidesColumns: true,
    };
  }

  private getFilterModel(filterId: string): FilterModelWithKeys {
    return {
      UserID: {
        filterType: 'number',
        type: 'equals',
        filter: filterId,
      },
    };
  }
}
